import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
} from "@material-ui/core";
import theme from "../../theme";
import CommonButton from "../button";

// Backend contract: `reason` is required, max 255 chars. Blocking the submit
// client-side per the spec so we don't count on the server for the empty case.
const MAX_LEN = 255;

const useStyles = makeStyles(() => ({
  paper: { borderRadius: 14, width: "100%", maxWidth: 480 },
  title: {
    padding: "18px 22px 8px",
    "& h2": {
      margin: 0,
      fontFamily: theme.fontFamily.MARKPROBOLD,
      color: theme.colors.Violet,
      fontSize: 17,
    },
  },
  subtitle: {
    fontFamily: theme.fontFamily.MARKPROBOOK,
    color: "#8892a4",
    fontSize: 12.5,
    marginTop: 2,
  },
  content: { padding: "8px 22px 4px" },
  field: {
    marginTop: 12,
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      fontSize: 13.5,
      fontFamily: theme.fontFamily.MARKPROBOOK,
      backgroundColor: theme.colors.White,
      "& fieldset": { borderColor: theme.colors.Gallery, borderWidth: 1.5 },
      "&:hover fieldset": { borderColor: theme.colors.Periwinkle },
      "&.Mui-focused fieldset": { borderColor: theme.colors.DodgerBlue },
    },
  },
  helperRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 6,
    padding: "0 2px",
  },
  helperText: {
    fontFamily: theme.fontFamily.MARKPROBOOK,
    fontSize: 11.5,
    color: "#8892a4",
  },
  counter: {
    fontFamily: theme.fontFamily.MARKPROBOLD,
    fontSize: 11.5,
    color: "#8892a4",
    fontVariantNumeric: "tabular-nums",
  },
  counterWarn: { color: "#B45309" },
  counterOver: { color: theme.colors.Monza },
  actions: { padding: "12px 22px 18px", gap: 10 },
}));

export default function RejectReasonModal({
  open,
  employeeName,
  onCancel,
  onConfirm,
  loading = false,
}) {
  const classes = useStyles();
  const [reason, setReason] = useState("");

  // Reset the field every time the modal closes/opens on a new record so a
  // partial reject reason can't leak from one application to the next.
  useEffect(() => {
    if (!open) setReason("");
  }, [open]);

  const trimmed = reason.trim();
  const length = reason.length;
  const isEmpty = trimmed.length === 0;
  const isOver = length > MAX_LEN;
  const canSubmit = !isEmpty && !isOver && !loading;

  const counterClass = isOver
    ? classes.counterOver
    : length > MAX_LEN * 0.85
    ? classes.counterWarn
    : "";

  const submit = () => {
    if (!canSubmit) return;
    onConfirm(trimmed);
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onCancel}
      classes={{ paper: classes.paper }}
      aria-labelledby="reject-join-request-title"
    >
      <DialogTitle
        id="reject-join-request-title"
        className={classes.title}
        disableTypography
      >
        <h2>Reject join request?</h2>
        <Typography className={classes.subtitle}>
          {employeeName
            ? `${employeeName} will receive this message and can resubmit.`
            : "The employee will receive this message and can resubmit."}
        </Typography>
      </DialogTitle>

      <DialogContent className={classes.content}>
        <TextField
          className={classes.field}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Explain why the request is being rejected — the employee sees this exact text."
          multiline
          minRows={4}
          fullWidth
          variant="outlined"
          autoFocus
        />
        <div className={classes.helperRow}>
          <span className={classes.helperText}>Required · shown to the employee</span>
          <span className={`${classes.counter} ${counterClass}`}>
            {length} / {MAX_LEN}
          </span>
        </div>
      </DialogContent>

      <DialogActions className={classes.actions}>
        <CommonButton
          type="button"
          text="Cancel"
          stylevariant="secondary2"
          onClick={onCancel}
          disabled={loading}
          style={{ padding: "8px 20px" }}
        />
        <CommonButton
          type="button"
          text={loading ? "Rejecting…" : "Reject"}
          stylevariant="danger"
          onClick={submit}
          disabled={!canSubmit}
          style={{ padding: "8px 22px" }}
        />
      </DialogActions>
    </Dialog>
  );
}
